import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Eye, FileText, ArrowRightLeft, Truck, Copy, Inbox, FileDown, Check, X, CalendarPlus, LayoutGrid, BadgeCheck, FileSpreadsheet, BookOpen, Wallet, FolderOpen } from "lucide-react";
import { api, API, fmtINR, fmtDate, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { canWrite, canDelete } from "../lib/permissions";
import { WorkerAdvanceDialog } from "./WorkerAdvanceDialog";
import DocumentsDialog, { DocumentsPanel } from "./DocumentsDialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

const qty = (v) => (v === null || v === undefined || v === "" ? "-" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 3 }));

function fmt(val, format) {
  if (format === "currency") return fmtINR(val);
  if (format === "date") return fmtDate(val);
  if (format === "qty") return qty(val);
  if (format === "number") return Number(val || 0).toLocaleString("en-IN");
  return val ?? "-";
}

const statusColor = (v) => {
  const s = String(v || "").toLowerCase();
  if (["paid", "delivered", "completed", "accepted", "active", "confirmed", "received", "in stock"].includes(s)) return "bg-emerald-100 text-emerald-700 border-emerald-200";
  if (["partial", "in transit", "sent", "pending", "draft", "low stock"].includes(s)) return "bg-amber-100 text-amber-700 border-amber-200";
  if (["unpaid", "cancelled", "rejected", "inactive", "expired", "out of stock"].includes(s)) return "bg-red-100 text-red-700 border-red-200";
  if (["customer", "purchase", "opening", "production"].includes(s)) return "bg-blue-100 text-blue-700 border-blue-200";
  return "bg-slate-100 text-slate-700 border-slate-200";
};

const VALIDATORS = {
  email: (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || "Enter a valid email",
  mobile: (v) => !v || /^[0-9]{7,15}$/.test(v.replace(/[+\s-]/g, "")) || "Enter a valid mobile number",
  gst: (v) => !v || /^[0-9A-Z]{15}$/.test(v.toUpperCase()) || "GST must be 15 characters",
  pan: (v) => !v || /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(v.toUpperCase()) || "PAN must be like ABCDE1234F",
  hsn_code: (v) => !v || /^[0-9]{4,8}$/.test(String(v).trim()) || "HSN must be 4 to 8 digits",
};

export default function CrudModule({ config }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [options, setOptions] = useState({});
  const [parties, setParties] = useState([]);
  const [deleteId, setDeleteId] = useState(null);
  const [payRow, setPayRow] = useState(null);
  const [advanceRow, setAdvanceRow] = useState(null);
  const [docsRow, setDocsRow] = useState(null);
  const [leaveBusy, setLeaveBusy] = useState("");
  const [payMode, setPayMode] = useState("Cash");
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [payBusy, setPayBusy] = useState(false);

  const convertSample = async (row) => {
    if (!window.confirm(`Create a permanent customer from "${row.customer_name}"?`)) return;
    try {
      const { data } = await api.post(`/samples/${row.id}/convert-customer`);
      toast.success(data.existing ? `Linked to existing customer ${data.customer_name}` : `Customer ${data.customer_name} created`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const doPay = async () => {    setPayBusy(true);
    try {
      await api.post(`/expenses/${payRow.id}/pay`, { payment_mode: payMode, payment_date: payDate });
      toast.success(`Marked paid via ${payMode}`);
      setPayRow(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setPayBusy(false);
  };

  const [errors, setErrors] = useState({});
  const [genOpen, setGenOpen] = useState(false);
  const [genMonth, setGenMonth] = useState("");
  const [genEmp, setGenEmp] = useState("all");
  const [genBusy, setGenBusy] = useState(false);
  const [regOpen, setRegOpen] = useState(false);
  const [regMonth, setRegMonth] = useState("");
  const navigate = useNavigate();
  const { user } = useAuth();
  const RES = { customers: "customers", suppliers: "suppliers", dispatches: "dispatches", payments: "payments", samples: "samples" };
  const resource = RES[config.endpoint] || config.endpoint;
  const canW = canWrite(user, resource);
  const canDel = canDelete(user, resource);
  const statusKey = config.statusKey || "status";

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/${config.endpoint}`); setItems(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setLoading(false);
  };

  useEffect(() => {
    load();
    config.fields.filter((f) => f.type === "select" && f.optionsEndpoint).forEach((f) => {
      api.get(`/${f.optionsEndpoint}`).then((r) => setOptions((o) => ({ ...o, [f.name]: r.data }))).catch(() => {});
    });
    if (config.fields.some((f) => f.type === "party")) api.get("/parties").then((r) => setParties(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [config.endpoint]);

  const optsFor = (f) => {
    let list = options[f.name] || [];
    if (f.activeOnly) list = list.filter((o) => String(o.status || "Active").toLowerCase() !== "inactive");
    return list;
  };

  const openCreate = () => { setEditing(null); setForm({ ...(config.defaults || {}) }); setErrors({}); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ ...row }); setErrors({}); setOpen(true); };

  const setField = (f, value) => {
    setForm((prev) => {
      const next = { ...prev, [f.name]: value };
      if (f.type === "select" && f.optionsEndpoint) {
        const opt = (options[f.name] || []).find((o) => o[f.optionValue || "id"] === value);
        if (opt) {
          if (f.linkName) next[f.linkName] = opt[f.optionLabel];
          if (f.copyFields) Object.entries(f.copyFields).forEach(([fk, ok]) => { if (opt[ok] != null) next[fk] = opt[ok]; });
        }
      }
      if (f.type === "party") {
        const p = parties.find((x) => x.id === value);
        if (p) { next.party_id = p.id; next.party_name = p.label; next.party_type = p.type; }
      }
      if (config.compute) Object.assign(next, config.compute(next));
      return next;
    });
    if (f.validate) setErrors((e) => ({ ...e, [f.name]: VALIDATORS[f.validate](value) === true ? null : VALIDATORS[f.validate](value) }));
  };

  const validate = () => {
    const errs = {};
    config.fields.forEach((f) => {
      const v = form[f.name];
      if (f.required && (v === undefined || v === null || v === "")) errs[f.name] = "Required";
      if (f.validate && v) { const r = VALIDATORS[f.validate](v); if (r !== true) errs[f.name] = r; }
      if ((f.name === "quantity") && v !== undefined && Number(v) <= 0) errs[f.name] = "Must be greater than 0";
      if (["rate", "tax_percent", "discount"].includes(f.name) && v !== undefined && Number(v) < 0) errs[f.name] = "Cannot be negative";
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const save = async () => {
    if (!validate()) { toast.error("Please fix the highlighted fields"); return; }
    try {
      if (editing) await api.put(`/${config.endpoint}/${editing.id}`, form);
      else await api.post(`/${config.endpoint}`, form);
      toast.success(`${config.title} ${editing ? "updated" : "created"}`);
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const doDelete = async () => {
    try { await api.delete(`/${config.endpoint}/${deleteId}`); toast.success("Deleted"); setDeleteId(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openPdf = (id) => window.open(`${API}/pdf/${config.pdfType}/${id}?token=${localStorage.getItem("token")}`, "_blank");
  const courierUrl = (courier, awb) => {
    const c = (courier || "").toLowerCase();
    if (!awb) return null;
    if (c.includes("blue dart")) return `https://www.bluedart.com/tracking?trackFor=0&trackNo=${awb}`;
    if (c.includes("delhivery")) return `https://www.delhivery.com/track/package/${awb}`;
    if (c.includes("dtdc")) return `https://www.dtdc.in/tracking.asp`;
    if (c.includes("fedex")) return `https://www.fedex.com/fedextrack/?trknbr=${awb}`;
    if (c.includes("india post")) return `https://www.indiapost.gov.in/`;
    return `https://www.google.com/search?q=${encodeURIComponent((courier || "") + " tracking " + awb)}`;
  };
  const track = (row) => {
    const url = courierUrl(row.courier, row.tracking_number);
    if (!url) { toast.error("No tracking / AWB number on this record"); return; }
    window.open(url, "_blank");
  };
  const copyAwb = (row) => {
    if (!row.tracking_number) { toast.error("No AWB to copy"); return; }
    navigator.clipboard?.writeText(row.tracking_number);
    toast.success(`Copied AWB ${row.tracking_number}`);
  };
  const convert = async (id) => {
    try { const { data } = await api.post(`/quotations/${id}/convert`); toast.success(`Converted to ${data.invoice_no}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const leaveAction = async (id, action) => {
    if (leaveBusy) return;
    setLeaveBusy(id + action);
    try {
      const { data } = await api.post(`/leaves/${id}/${action}`, {});
      toast.success(data.warning || `Leave ${data.status}${data.attendance_marked ? ` · ${data.attendance_marked} attendance day(s) marked` : ""}`);
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d ? apiError(d) : `Could not ${action} this leave request (server error ${e.response?.status || "unreachable"}). Please try again or contact support.`);
    }
    setLeaveBusy("");
  };

  const runGenerate = async () => {
    if (!genMonth) { toast.error("Please select a month"); return; }
    setGenBusy(true);
    try {
      const body = { month: genMonth };
      if (genEmp !== "all") body[config.generate.idsKey] = [genEmp];
      const { data } = await api.post(`/${config.generate.endpoint}`, body);
      toast.success(data.message || "Generated successfully");
      setGenOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setGenBusy(false);
  };

  const markPaid = async (row) => {
    try {
      await api.put(`/${config.endpoint}/${row.id}`, { status: "Paid", payment_date: new Date().toISOString().slice(0, 10), paid_at: new Date().toISOString() });
      toast.success("Marked as Paid"); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openRegister = (fmt) => {
    if (!regMonth) { toast.error("Please select a month"); return; }
    const token = localStorage.getItem("token");
    const url = fmt === "pdf"
      ? `${API}/pdf/${config.register.pdf}?token=${token}&month=${regMonth}`
      : `${API}/${config.register.xlsx}?token=${token}&month=${regMonth}`;
    window.open(url, "_blank");
  };

  const filtered = useMemo(() => {
    let out = items;
    if (query) { const q = query.toLowerCase(); out = out.filter((it) => (config.searchKeys || []).some((k) => String(it[k] || "").toLowerCase().includes(q))); }
    if (statusFilter !== "all") out = out.filter((it) => String(it[statusKey] || "").toLowerCase() === statusFilter.toLowerCase());
    return out;
  }, [items, query, statusFilter, config.searchKeys, statusKey]);

  return (
    <div data-testid={`module-${config.endpoint}`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-head">{config.title}</h1>
          <p className="text-sm text-slate-500 mt-0.5">{filtered.length} record{filtered.length !== 1 ? "s" : ""}{config.note ? ` · ${config.note}` : ""}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid={`search-${config.endpoint}`} placeholder="Search..." value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9 w-full sm:w-52 bg-white" />
          </div>
          {config.statusFilter && (
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid={`status-filter-${config.endpoint}`} className="w-36 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {config.statusFilter.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {config.bulkPath && canW && (
            <Button data-testid="bulk-attendance-btn" variant="outline" onClick={() => navigate(config.bulkPath)} className="rounded-sm gap-1.5 shrink-0">
              <LayoutGrid className="w-4 h-4" /> <span className="hidden sm:inline">Bulk Mark</span>
            </Button>
          )}
          {config.generate && canW && (
            <Button data-testid={`generate-${config.endpoint}-btn`} variant="outline" onClick={() => { setGenMonth(""); setGenEmp("all"); setGenOpen(true); }} className="rounded-sm gap-1.5 shrink-0">
              <CalendarPlus className="w-4 h-4" /> <span className="hidden sm:inline">{config.generate.label}</span>
            </Button>
          )}
          {config.register && canW && (
            <Button data-testid="wage-register-btn" variant="outline" onClick={() => { setRegMonth(""); setRegOpen(true); }} className="rounded-sm gap-1.5 shrink-0">
              <FileSpreadsheet className="w-4 h-4" /> <span className="hidden sm:inline">{config.register.label}</span>
            </Button>
          )}
          {canW && (
            <Button data-testid={`add-${config.endpoint}-btn`} onClick={openCreate} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5 shrink-0">
              <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Add New</span>
            </Button>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left">
                {config.columns.map((c) => <th key={c.key} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">{c.label}</th>)}
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(6)].map((_, ri) => (
                  <tr key={`sk-${ri}`} className="border-b border-slate-100" data-testid="table-skeleton-row">
                    {config.columns.map((c, ci) => (
                      <td key={c.key} className="px-4 py-3.5"><div className="ab-skeleton h-4" style={{ width: `${[70, 45, 60, 40, 55][ci % 5]}%` }} /></td>
                    ))}
                    <td className="px-4 py-3.5"><div className="ab-skeleton h-4 w-16 ml-auto" /></td>
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr><td colSpan={config.columns.length + 1} className="px-4 py-16 text-center" data-testid="table-empty-state">
                  <div className="flex flex-col items-center gap-2 text-slate-400">
                    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center"><Inbox className="w-6 h-6 text-slate-300" /></div>
                    <span className="text-sm font-medium text-slate-500">No records found</span>
                    {canW && <span className="text-xs text-slate-400">Click "Add New" to create your first {config.title?.toLowerCase()}.</span>}
                  </div>
                </td></tr>
              ) : filtered.map((row) => (
                <tr key={row.id} data-testid={`row-${config.endpoint}-${row.id}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  {config.columns.map((c) => (
                    <td key={c.key} className={`px-4 py-3 whitespace-nowrap ${["currency", "number", "qty"].includes(c.format) ? "font-mono tabular-nums text-slate-900" : "text-slate-700"}`}>
                      {c.format === "badge" ? <Badge variant="outline" className={`rounded-full font-medium capitalize px-2.5 ${statusColor(row[c.key])}`}>{row[c.key] || "-"}</Badge> : fmt(row[c.key], c.format)}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <div className="flex items-center justify-end gap-1">
                      {config.convertible && !row.invoice_id && (
                        <Button data-testid={`convert-${row.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-emerald-600" title="Convert to Invoice" onClick={() => convert(row.id)}><ArrowRightLeft className="w-4 h-4" /></Button>
                      )}
                      {config.receiptAction && row.party_type === "customer" && (
                        <Button data-testid={`receipt-${row.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-emerald-600" title="Download Receipt" onClick={() => window.open(`${API}/pdf/receipt/${row.id}?token=${localStorage.getItem("token")}`, "_blank")}><FileText className="w-4 h-4" /></Button>
                      )}
                      {config.sampleConvert && !row.customer_id && canW && (
                        <Button data-testid={`convert-customer-${row.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-emerald-600" title="Convert to Customer" onClick={() => convertSample(row)}><ArrowRightLeft className="w-4 h-4" /></Button>
                      )}
                      {config.pdfType && (
                        <Button data-testid={`pdf-${row.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-[#0B132B]" title="View PDF" onClick={() => openPdf(row.id)}><FileText className="w-4 h-4" /></Button>
                      )}
                      {config.trackShipment && (
                        <>
                          <Button data-testid={`track-${row.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-blue-600" title="Track Shipment" onClick={() => track(row)}><Truck className="w-4 h-4" /></Button>
                          <Button data-testid={`copy-awb-${row.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-[#0B132B]" title="Copy AWB" onClick={() => copyAwb(row)}><Copy className="w-4 h-4" /></Button>
                        </>
                      )}
                      {config.leaveActions && row.status === "Pending" && canW && (
                        <>
                          <Button data-testid={`approve-${row.id}`} disabled={!!leaveBusy} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-emerald-600 disabled:opacity-40" title="Approve Leave" onClick={() => leaveAction(row.id, "approve")}><Check className="w-4 h-4" /></Button>
                          <Button data-testid={`reject-${row.id}`} disabled={!!leaveBusy} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-red-600 disabled:opacity-40" title="Reject Leave" onClick={() => leaveAction(row.id, "reject")}><X className="w-4 h-4" /></Button>
                        </>
                      )}
                      {config.slipAction && (
                        <Button data-testid={`slip-${row.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-[#0B132B]" title="Download Slip PDF" onClick={() => window.open(`${API}/pdf/${typeof config.slipAction === "string" ? config.slipAction : "salary"}/${row.id}?token=${localStorage.getItem("token")}`, "_blank")}><FileText className="w-4 h-4" /></Button>
                      )}
                      {config.markPaid && canW && String(row.status).toLowerCase() !== "paid" && (
                        <Button data-testid={`mark-paid-${row.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-emerald-600" title="Mark as Paid" onClick={() => markPaid(row)}><BadgeCheck className="w-4 h-4" /></Button>
                      )}
                      {config.payAction && canW && String(row.status).toLowerCase() !== "paid" && (
                        <Button data-testid={`pay-${row.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-emerald-600" title="Mark as Paid" onClick={() => { setPayRow(row); setPayMode(row.payment_mode || "Cash"); }}><BadgeCheck className="w-4 h-4" /></Button>
                      )}
                      {config.advanceAction && (
                        <Button data-testid={`advance-${row.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-amber-600" title="Advances given / pending" onClick={() => setAdvanceRow(row)}><Wallet className="w-4 h-4" /></Button>
                      )}
                      {config.statementAction && (
                        <Button data-testid={`statement-${row.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-[#0B132B]" title="Download Statement" onClick={() => window.open(`${API}/${config.statementAction}/${row.id}/statement/pdf?token=${localStorage.getItem("token")}`, "_blank")}><FileDown className="w-4 h-4" /></Button>
                      )}
                      {config.rowPdfPath && (
                        <Button data-testid={`row-pdf-${row.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-[#0B132B]" title="Download Details PDF" onClick={() => window.open(`${API}/${config.rowPdfPath}/${row.id}/pdf?token=${localStorage.getItem("token")}`, "_blank")}><FileDown className="w-4 h-4" /></Button>
                      )}
                      {config.documentsType && (
                        <Button data-testid={`docs-${row.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-[#0B132B]" title="Documents" onClick={() => setDocsRow(row)}><FolderOpen className="w-4 h-4" /></Button>
                      )}
                      {config.ledgerType && (
                        <Button data-testid={`ledger-${row.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-[#0B132B]" title="Party Ledger" onClick={() => navigate(`/ledger/${config.ledgerType}/${row.id}`)}><BookOpen className="w-4 h-4" /></Button>
                      )}
                      {config.detailPath && (
                        <Button data-testid={`view-${row.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-[#0B132B]" onClick={() => navigate(`${config.detailPath}/${row.id}`)}><Eye className="w-4 h-4" /></Button>
                      )}
                      {canW && (
                        <Button data-testid={`edit-${row.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-[#0B132B]" onClick={() => openEdit(row)}><Pencil className="w-4 h-4" /></Button>
                      )}
                      {canDel && (
                        <Button data-testid={`delete-${row.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-red-600" onClick={() => setDeleteId(row.id)}><Trash2 className="w-4 h-4" /></Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{editing ? "Edit" : "New"} {config.title}</DialogTitle><DialogDescription className="sr-only">Fill in the fields and save.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            {config.fields.filter((f) => !f.showIf || f.showIf(form)).map((f) => {
              const full = f.type === "textarea";
              const err = errors[f.name];
              return (
                <div key={f.name} className={`space-y-1.5 ${full ? "sm:col-span-2" : ""}`}>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{f.label}{f.required && <span className="text-red-500"> *</span>}</label>
                  {f.type === "textarea" ? (
                    <Textarea data-testid={`field-${f.name}`} value={form[f.name] || ""} onChange={(e) => setField(f, e.target.value)} className="bg-white" />
                  ) : f.type === "party" ? (
                    <Select value={form.party_id || ""} onValueChange={(v) => setField(f, v)}>
                      <SelectTrigger data-testid={`field-${f.name}`} className="bg-white"><SelectValue placeholder="Select party" /></SelectTrigger>
                      <SelectContent>{parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.label} ({p.type})</SelectItem>)}</SelectContent>
                    </Select>
                  ) : f.type === "boolean" ? (
                    <Select value={form[f.name] ? "yes" : "no"} onValueChange={(v) => setForm((p) => ({ ...p, [f.name]: v === "yes" }))}>
                      <SelectTrigger data-testid={`field-${f.name}`} className="bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
                    </Select>
                  ) : f.type === "select" ? (
                    <Select value={form[f.name] || ""} onValueChange={(v) => setField(f, v)}>
                      <SelectTrigger data-testid={`field-${f.name}`} className="bg-white"><SelectValue placeholder={`Select ${f.label}`} /></SelectTrigger>
                      <SelectContent>
                        {f.optionsEndpoint
                          ? optsFor(f).map((o) => <SelectItem key={o[f.optionValue || "id"]} value={o[f.optionValue || "id"]}>{o[f.optionLabel]}</SelectItem>)
                          : (f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input data-testid={`field-${f.name}`} type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} step={f.step} min={f.min} readOnly={f.readOnly} value={form[f.name] ?? ""} onChange={(e) => setField(f, e.target.value)} className={`bg-white ${f.readOnly ? "bg-slate-50 text-slate-500 cursor-not-allowed" : ""}`} />
                  )}
                  {err && <p className="text-xs text-red-500">{err}</p>}
                </div>
              );
            })}
          </div>
          {config.documentsType && (
            <div className="border-t border-slate-200 pt-4 mt-2" data-testid="form-documents-section">
              <h3 className="text-sm font-semibold text-slate-800 mb-2">Documents &amp; Attachments</h3>
              {editing ? (
                <DocumentsPanel recordType={config.documentsType} recordId={editing.id} compact />
              ) : (
                <p className="text-xs text-slate-500" data-testid="form-documents-hint">
                  Save this record first — you can then attach bills, warranty cards, certificates, insurance and service records (PDF, JPG, PNG).
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="save-btn" onClick={save} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm">{editing ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-head">{config.generate?.label || "Generate"}</DialogTitle><DialogDescription>Auto-calculates from attendance for the selected month. Existing months are never overwritten.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Month <span className="text-red-500">*</span></label>
              <Input data-testid="gen-month" type="month" value={genMonth} onChange={(e) => setGenMonth(e.target.value)} className="bg-white" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{config.generate?.optionsKey === "worker_id" ? "Workers" : "Employees"}</label>
              <Select value={genEmp} onValueChange={setGenEmp}>
                <SelectTrigger data-testid="gen-employee" className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{config.generate?.optionsKey === "worker_id" ? "All Active Workers" : "All Active Employees"}</SelectItem>
                  {(options[config.generate?.optionsKey] || []).map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="gen-run-btn" onClick={runGenerate} disabled={genBusy} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm">{genBusy ? "Generating..." : "Generate"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={regOpen} onOpenChange={setRegOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-head">{config.register?.label}</DialogTitle><DialogDescription>Download the full month's wage register (all workers) for cashier payout.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Month <span className="text-red-500">*</span></label>
              <Input data-testid="reg-month" type="month" value={regMonth} onChange={(e) => setRegMonth(e.target.value)} className="bg-white" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="reg-pdf-btn" onClick={() => openRegister("pdf")} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5"><FileText className="w-4 h-4" /> PDF</Button>
            <Button data-testid="reg-xlsx-btn" onClick={() => openRegister("xlsx")} variant="outline" className="rounded-sm gap-1.5"><FileSpreadsheet className="w-4 h-4" /> Excel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!payRow} onOpenChange={(o) => !o && setPayRow(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-head">Mark as Paid</DialogTitle>
            <DialogDescription>Choose how it was paid — Cash comes out of Cash In Hand, any other mode out of the Bank account.</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex justify-between text-sm border-b border-slate-100 pb-2">
              <span className="text-slate-500">{payRow?.category || payRow?.description}</span>
              <span className="font-mono font-semibold text-slate-900">{fmtINR(payRow?.amount || 0)}</span>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Paid Via</label>
              <Select value={payMode} onValueChange={setPayMode}>
                <SelectTrigger data-testid="pay-mode-select" className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>{["Cash", "Bank Transfer", "Cheque", "UPI", "NEFT/RTGS"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Payment Date</label>
              <Input data-testid="pay-date-input" type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} className="bg-white" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayRow(null)} className="rounded-sm">Cancel</Button>
            <Button data-testid="pay-confirm-btn" onClick={doPay} disabled={payBusy} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm gap-1.5"><BadgeCheck className="w-4 h-4" /> {payBusy ? "Saving..." : "Mark Paid"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete record?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone. For financial documents, consider marking as Cancelled instead.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-btn" onClick={doDelete} className="bg-red-600 hover:bg-red-700 rounded-sm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {advanceRow && <WorkerAdvanceDialog worker={advanceRow} basePath={typeof config.advanceAction === "string" ? config.advanceAction : config.endpoint} canWrite={canW} onClose={() => setAdvanceRow(null)} />}
      {docsRow && <DocumentsDialog recordType={config.documentsType} recordId={docsRow.id} title={docsRow[config.refKey] || docsRow.reference_number || ""} onClose={() => setDocsRow(null)} />}
    </div>
  );
}
